<template lang="pug">
.send-message-detail
  .contents.d-flex.flex-row
    .send-message.flex-grow-1
      SendMessageDetailContent(v-model="message" :readCount="readCount" :unreadCount="unreadCount"
        @onSendTargetList="onSendTargetList" @onPreview="onPreview" @onRead="onRead" @onUnread="onUnread")
    .reply-messages.flex-grow-1
      .reply-list(v-show="showReplyList")
        SendMessageReplyListTable(v-model="replyItems" :fields="replyFields" :isLoading="replyLoading" :type="1" @onMessage="onMessage" @onReadSwitch="onReadSwitch")
      .reply-detail(v-show="showReplyDetail")
        SendMessageReplyDetailBox(v-model="replyDetailModalText" :message="replyDetailModalMessage" @onReply="onReply" @onBack="onBack")

  SendMessageFilePreviewModal(ref="previewModal")
  SendMessageOtherUserGroupReadMembersModal(v-model="showSendMessageReadOtherUsersModal" :otherUsers="sendMessageReadMembers"
    :csvTypeName="sendMessageCsvTypeName" :sendMessageTitle="sendMessageTitle" :showReadDatetime="showReadDatetime")

  OkCancelModal(v-model="showSendMessageModal" title="メッセージを送信しますか？" @ok="registerReplyMessage(replyTargetItem)")
  OkModal(v-model="showSendMessageSuccessModal" title="メッセージ送信を受け付けました。" @ok="onReplySuccessOk")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Prop, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import Setting from "../../../../public/setting.json";
import S3UrlService from "@/commons/services/S3Url";
import MessageModel from "@/commons/models/Message";
import OtherUserGroupSendMessageReadCountsService, { SendMessageReadCountsModel } from "@/commons/services/OtherUserGroupSendMessageReadCounts";
import OtherUserGroupSendMessageReadMembersService, { OtherUserGroupSendMessageReadMemberModel } from "@/commons/services/OtherUserGroupSendMessageReadMembers";
import OtherUserGroupTypesService from "@/commons/services/OtherUserGroupTypes";
import OtherUserGroupTypeModel from "@/commons/models/OtherUserGroupType";
import OtherUserGroupSendMessageRepliesService, {
  OtherUserGroupSendMessageRepliesPostModel,
  SendMessageReplyModel,
  SendMessageReplySenderType
} from "@/commons/services/OtherUserGroupSendMessageReplies";
import OtherUserGroupSendMessageRepliesMembersService, { SendMessageRepliesMemberPatchModel } from "@/commons/services/OtherUserGroupSendMessageRepliesMembers";
import OtherUserGroupMembersService from "@/commons/services/OtherUserGroupMembers";
import OtherUserGroupModel from "@/commons/models/OtherUserGroup";

import PageLoading from "@/pc/basics/PageLoading.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import SendMessageDetailContent from "@/pc/containers/sendMessages/SendMessageDetailContent.vue";
import SendMessageReplyListTable, {
  SendMessageReplyListTableItem,
  SendMessageReplyListTableField,
  SendMessageReplyListTableType
} from "@/pc/containers/sendMessages/SendMessageReplyListTable.vue";
import SendMessageReplyDetailBox from "@/pc/containers/sendMessages/SendMessageReplyDetailBox.vue";

import SendMessageFilePreviewModal from "@/pc/components/modals/SendMessageFilePreviewModal.vue";
import SendMessageOtherUserGroupReadMembersModal from "@/pc/components/modals/SendMessageOtherUserGroupReadMembersModal.vue";
import { SenderType } from "@/commons/models/Resident";

/**
 * メッセージ送信詳細コンポーネント
 */
@Component({
  components: {
    PageLoading,
    OkCancelModal,
    OkModal,
    SendMessageFilePreviewModal,
    SendMessageDetailContent,
    SendMessageOtherUserGroupReadMembersModal,
    SendMessageReplyListTable,
    SendMessageReplyDetailBox
  }
})
export default class SendMessageOtherUserDetailTab extends Vue {
  /** 送信メッセージにひもづくメンバーサービス */
  @Inject("OtherUserGroupSendMessageReadMembersService") otherUserGroupSendMessageReadMembersService!: OtherUserGroupSendMessageReadMembersService;
  /** メッセージ(既読/未読)サービス */
  @Inject("OtherUserGroupSendMessageReadCountsService") otherUserGroupSendMessageReadCountsService!: OtherUserGroupSendMessageReadCountsService;
  /** 施設関係者グループ種類サービス */
  @Inject("OtherUserGroupTypesService") otherUserGroupTypesService!: OtherUserGroupTypesService;
  /** メッセージ送信の返信サービス */
  @Inject("OtherUserGroupSendMessageRepliesService") otherUserGroupSendMessageRepliesService!: OtherUserGroupSendMessageRepliesService;
  /** メッセージ送信の返信サービス(施設関係者グループメンバー) */
  @Inject("OtherUserGroupSendMessageRepliesMembersService") otherUserGroupSendMessageRepliesMembersService!: OtherUserGroupSendMessageRepliesMembersService;
  /** S3Urlサービス */
  @Inject("S3UrlService") s3UrlService!: S3UrlService;
  /** 施設関係者グループメンバーサービス */
  @Inject("OtherUserGroupMembersService") otherUserGroupMembersService!: OtherUserGroupMembersService;

  /** 表示対象のメッセージ */
  @Prop({
    required: true
  })
  message!: MessageModel;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** メッセージ送信確認モーダル表示可否 */
  showSendMessageModal: boolean = false;
  /** メッセージ送信完了モーダル表示可否 */
  showSendMessageSuccessModal: boolean = false;

  /** 返信リストの表示可否 */
  showReplyList: boolean = true;
  /** 返信列名 */
  replyFields: Array<SendMessageReplyListTableField> = [{ key: "message", label: "", sortable: false, sortDirection: "desc", class: "message" }];
  /** 返信メッセージ */
  replyItems: Array<SendMessageReplyListTableItem> = [];
  /** 返信のローディング表示 */
  replyLoading: boolean = true;
  /** 施設関係者グループ種類MAP */
  otherUserGroupTypeMap: Map<number, string> = new Map<number, string>();

  /** 返信メッセージの詳細・返信の表示可否 */
  showReplyDetail: boolean = false;
  /** 返信メッセージの詳細・返信で入力する返信内容 */
  replyDetailModalText: string | null = null;
  /** 返信メッセージの詳細・返信で表示するメッセージ */
  replyDetailModalMessage: SendMessageReplyListTableItem | null = null;
  /** 返信メッセージ送信対象のメッセージ */
  replyTargetItem: SendMessageReplyListTableItem | null = null;

  /** 送信メッセージの既読数 */
  readCount: number = -1;
  /** 送信メッセージの未読数 */
  unreadCount: number = -1;

  /** 既読／未読メンバーユーザーモーダル表示可否 */
  showSendMessageReadOtherUsersModal: boolean = false;
  /** 未読施設関係者グループメンバーユーザーモーダルに渡す施設関係者グループメンバー */
  sendMessageReadMembers: Array<OtherUserGroupSendMessageReadMemberModel> = [];
  /** 既読／未読家族どちらか */
  sendMessageCsvTypeName: string = "";
  /** CSV出力時に使用するメッセージの件名 */
  sendMessageTitle: string = "";
  /** 既読時間を表示するか否か */
  showReadDatetime: boolean = false;

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";
    await this.$timerUtil.setTimeout(2000);
    await this.createView();
    this.isBusy = false;
    return;
  }

  /**
   * ### 画面表示情報を作成する
   */
  public async createView(): Promise<void> {
    this.replyLoading = true;

    // 送信メッセージの既読/未読数を取得する
    const readModel: SendMessageReadCountsModel = await this.getSendMessageRead();
    this.readCount = readModel.readCounts;
    this.unreadCount = readModel.unreadCounts;

    // 後で使用するため、施設関係者グループ種類をMAP化しておく
    const otherUserGroupTypes: Array<OtherUserGroupTypeModel> = await this.getOtherGroupTypes();
    const otherUserGroupTypeMap: Map<number, string> = this.createOtherUserGroupTypesMap(otherUserGroupTypes);
    this.otherUserGroupTypeMap = otherUserGroupTypeMap;

    // 返信リストに表示する返信を取得する
    const replyMessages: Array<SendMessageReplyModel> = await this.getSendMessageReplies(Number(this.message.datetime));
    const replyItems: Array<SendMessageReplyListTableItem> = this.createReplyItems(replyMessages, otherUserGroupTypeMap);
    this.replyItems = replyItems;

    this.replyLoading = false;

    return;
  }

  /**
   * ### 送信メッセージの既読/未読数を取得する
   */
  public async getSendMessageRead(): Promise<SendMessageReadCountsModel> {
    const message: MessageModel = this.message;
    const readModel: SendMessageReadCountsModel = await this.otherUserGroupSendMessageReadCountsService.get(userStore, Number(message.datetime));

    return readModel;
  }

  /**
   * ### 施設関係者グループ種類を取得する
   */
  public async getOtherGroupTypes(): Promise<Array<OtherUserGroupTypeModel>> {
    const otherUserGroupTypes: Array<OtherUserGroupTypeModel> = await this.otherUserGroupTypesService.get(userStore);

    return otherUserGroupTypes;
  }

  /**
   * ### 画面表示用に施設関係者グループ種類のMAPを作成する
   */
  public createOtherUserGroupTypesMap(otherUserGroupTypes: Array<OtherUserGroupTypeModel>): Map<number, string> {
    let map: Map<number, string> = new Map<number, string>();

    for (const type of otherUserGroupTypes) {
      map.set(type.id, type.name);
    }

    return map;
  }

  /**
   * ### メッセージの返信を取得する
   */
  public async getSendMessageReplies(messageDatetime: number): Promise<Array<SendMessageReplyModel>> {
    const response: Array<SendMessageReplyModel> = await this.otherUserGroupSendMessageRepliesService.get(userStore, messageDatetime);
    return response;
  }

  /**
   * ### 返信リストの行情報を作成する
   */
  public createReplyItems(replies: Array<SendMessageReplyModel>, otherUserGroupTypeMap: Map<number, string>): Array<SendMessageReplyListTableItem> {
    // 返信メッセージの1行はユーザーの1番古いデータになるので、datetimeの古い順にソートする
    const sortedReplies: Array<SendMessageReplyModel> = replies.sort((a, b) => a.datetime - b.datetime);

    // ユーザー単位でメッセージをMAP化する
    let map: Map<string, Array<SendMessageReplyModel>> = new Map<string, Array<SendMessageReplyModel>>();
    for (const reply of sortedReplies) {
      const messages: Array<SendMessageReplyModel> | undefined = map.get(`${reply.groupId}_${reply.memberId}`);
      if (messages) {
        messages.push(reply);
        map.set(`${reply.groupId}_${reply.memberId}`, messages);
      } else {
        map.set(`${reply.groupId}_${reply.memberId}`, [reply]);
      }
    }

    // MAP化したものを行情報にする
    let items: Array<SendMessageReplyListTableItem> = [];
    map.forEach((value: Array<SendMessageReplyModel>) => {
      const sortedValue: Array<SendMessageReplyModel> = value.sort((a, b) => a.datetime - b.datetime);

      // 行の並び順は最後のメッセージの日付の新しいもの順にしたいので、行情報は最新のものを使用する
      const index: number = sortedValue.length - 1;

      // 未読かどうかは家族の返信で未読が存在するか否かで判断する
      let readed: boolean = true;
      for (const value of sortedValue) {
        if (value.senderType == SendMessageReplySenderType.OTHER_USER && value.readUserIds.indexOf(userStore.loginUser.userId) < 0) {
          readed = false;
          break;
        }
      }

      // 返信のグループ名を格納する
      const groupTypeName: string | undefined = otherUserGroupTypeMap.get(sortedValue[index].groupTypeId);

      items.push({
        parentMessagesSortKey: sortedValue[index].parentMessagesSortKey,
        groupId: sortedValue[index].groupId,
        memberId: sortedValue[index].memberId,
        mainName: sortedValue[index].groupMemberName,
        subName: `${groupTypeName ? groupTypeName : "未入力"}・${sortedValue[index].groupName}・${sortedValue[index].groupMemberOrganization}`,
        datetime: sortedValue[index].datetime,
        readed: readed,
        type: sortedValue[index].type as SendMessageReplyListTableType,
        messages: value,
        filterKey1: sortedValue[index].groupName,
        filterKey2: groupTypeName ? groupTypeName : "未入力",
        filterKey3: sortedValue[index].groupMemberName
      });
    });

    // 画面表示では日付の新しい順なのでソートしなおす
    const sortedItems: Array<SendMessageReplyListTableItem> = items.concat().sort((a, b) => b.datetime - a.datetime);

    return sortedItems;
  }

  /**
   * ### 宛先リストを表示する
   */
  public async onSendTargetList(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const targetsMembers: Array<OtherUserGroupSendMessageReadMemberModel> = [];
    const members: Array<OtherUserGroupSendMessageReadMemberModel> = await this.getSendMessageReadMembers();
    for (let member of members) {
      const _member = member as OtherUserGroupSendMessageReadMemberModel;
      const typeName: string | undefined = this.otherUserGroupTypeMap.get(_member.groupTypeId);
      _member.groupTypeName = typeName ? typeName : "";
      targetsMembers.push(_member);
    }
    this.sendMessageCsvTypeName = "宛先一覧";
    this.sendMessageTitle = this.message.functionContents.title;
    this.sendMessageReadMembers = targetsMembers;
    this.showReadDatetime = true;

    this.isBusy = false;

    this.showSendMessageReadOtherUsersModal = true;

    return;
  }

  /**
   * ### プレビューボタン押下イベント処理
   *
   * - ダイアログで送信メッセージの添付ファイルをプレビュー表示する
   */
  public async onPreview(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const contents: any = this.message.functionContents;
    let files: Array<any> = [];
    if (contents.fileUrls) {
      for (let fileUrl of contents.fileUrls) {
        const file: File = await this.getS3File(fileUrl);
        files.push(file);
      }
    }

    const modal: SendMessageFilePreviewModal | Vue | Element | Vue[] | Element[] = this.$refs.previewModal;
    if (modal instanceof SendMessageFilePreviewModal) {
      modal.show("", false, files);
    }

    this.isBusy = false;

    return;
  }

  /**
   * ### S3に格納されているFileを取得する
   *
   * @param url 元URL
   */
  public async getS3File(url: string): Promise<File> {
    const response: File = await this.s3UrlService.getFile(userStore, Setting.backets.imageExpireHour, url);

    return response;
  }

  /**
   * ### 返信メッセージを既読にする
   */
  public async onMessage(item: SendMessageReplyListTableItem): Promise<void> {
    const member: OtherUserGroupModel | null = await this.getOtherUserGroupMember(item.groupId as number, item.memberId as number);
    item.isDeleted = member == null ? true : false;

    this.replyDetailModalMessage = item;
    this.replyDetailModalText = "";
    this.showReplyList = false;
    this.showReplyDetail = true;

    for (const message of item.messages as Array<SendMessageReplyModel>) {
      if (message.readUserIds.indexOf(userStore.loginUser.userId) < 0 ? true : false) {
        const request: SendMessageRepliesMemberPatchModel = {
          readUserId: userStore.loginUser.userId,
          readUserName: userStore.loginUser.name,
          read: true
        };

        await this.otherUserGroupSendMessageRepliesMembersService.patch(userStore, Number(this.message.datetime), message.groupId, message.memberId, message.datetime, request);

        message.readUserIds.push(userStore.loginUser.userId);
        item.readed = true;
      }
    }

    return;
  }

  /**
   * ### 施設関係者グループメンバーを取得する
   *
   * - 施設関係者グループメンバー単一を取得するAPIはないので、施設関係者グループにひもづくメンバーを取得する
   * - 施設関係者グループメンバーの中からメンバーIDが同一のものを修正対象のメンバーと判断する
   */
  public async getOtherUserGroupMember(groupId: number, memberId: number): Promise<OtherUserGroupModel | null> {
    const members: Array<OtherUserGroupModel> = await this.otherUserGroupMembersService.get(userStore, groupId);

    for (const member of members) {
      if (member.memberId == memberId) {
        return member;
      }
    }
    return null;
  }

  public onReply(item: SendMessageReplyListTableItem): void {
    this.showSendMessageModal = true;
    this.replyTargetItem = item;
    return;
  }

  public async registerReplyMessage(item: SendMessageReplyListTableItem): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "送信中...";

    const requestBody: OtherUserGroupSendMessageRepliesPostModel = {
      message: this.replyDetailModalText as string
    };

    // 送信種別に応じて呼び出すサービスを切り替える
    if (this.message.senderType == SenderType.LINE) {
      await this.otherUserGroupSendMessageRepliesService.post(userStore, item.parentMessagesSortKey, item.groupId as number, item.memberId as number, requestBody);
    } else {
      await this.otherUserGroupSendMessageRepliesService.postEMail(userStore, item.parentMessagesSortKey, item.groupId as number, item.memberId as number, requestBody);
    }

    this.isBusy = false;

    // メッセージ送信成功ダイアログを表示する
    this.showSendMessageSuccessModal = true;

    return;
  }

  public async onReplySuccessOk(): Promise<void> {
    await this.createView();

    const selectedItem: SendMessageReplyListTableItem = this.replyDetailModalMessage as SendMessageReplyListTableItem;

    for (const item of this.replyItems) {
      if (item.groupId == selectedItem.groupId && item.memberId == selectedItem.memberId) {
        this.replyDetailModalMessage = item;
        this.replyDetailModalText = "";
      }
    }

    return;
  }

  public onBack(): void {
    this.showReplyDetail = false;
    this.showReplyList = true;
    return;
  }

  /**
   * ### 既読リストを表示する
   */
  public async onRead(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const members: Array<OtherUserGroupSendMessageReadMemberModel> = await this.getSendMessageReadMembers();
    const readMembers: Array<OtherUserGroupSendMessageReadMemberModel> = await this.createRreadMembers(members);
    this.sendMessageCsvTypeName = "既読一覧";
    this.sendMessageTitle = this.message.functionContents.title;
    this.sendMessageReadMembers = readMembers;
    this.showReadDatetime = true;

    this.isBusy = false;

    this.showSendMessageReadOtherUsersModal = true;

    return;
  }

  /**
   * ### 未読リストを表示する
   */
  public async onUnread(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const members: Array<OtherUserGroupSendMessageReadMemberModel> = await this.getSendMessageReadMembers();
    const unreadMembers: Array<OtherUserGroupSendMessageReadMemberModel> = await this.createUnreadMembers(members);
    this.sendMessageCsvTypeName = "未読一覧";
    this.sendMessageTitle = this.message.functionContents.title;
    this.sendMessageReadMembers = unreadMembers;
    this.showReadDatetime = false;

    this.isBusy = false;

    this.showSendMessageReadOtherUsersModal = true;

    return;
  }

  /**
   * ### メッセージ送信したメッセージにひもづく施設関係者グループメンバーを取得する
   */
  public async getSendMessageReadMembers(): Promise<Array<OtherUserGroupSendMessageReadMemberModel>> {
    const members: Array<OtherUserGroupSendMessageReadMemberModel> = await this.otherUserGroupSendMessageReadMembersService.get(userStore, Number(this.message.datetime));

    return members;
  }

  /**
   * ### メッセージ送信したメッセージにひもづく施設関係者グループメンバーから既読の施設関係者グループメンバーを返却する
   */
  public createRreadMembers(members: Array<OtherUserGroupSendMessageReadMemberModel>): Array<OtherUserGroupSendMessageReadMemberModel> {
    let readMembers: Array<OtherUserGroupSendMessageReadMemberModel> = [];

    for (let member of members) {
      if (member.readDatetime != undefined || member.readDatetime != null) {
        const _member = member as OtherUserGroupSendMessageReadMemberModel;
        const typeName: string | undefined = this.otherUserGroupTypeMap.get(_member.groupTypeId);
        _member.groupTypeName = typeName ? typeName : "";

        readMembers.push(_member);
      }
    }

    return readMembers;
  }

  /**
   * ### メッセージ送信したメッセージにひもづく施設関係者グループメンバーから未読の施設関係者グループメンバーを返却する
   */
  public createUnreadMembers(members: Array<OtherUserGroupSendMessageReadMemberModel>): Array<OtherUserGroupSendMessageReadMemberModel> {
    let unreadMembers: Array<OtherUserGroupSendMessageReadMemberModel> = [];

    for (let member of members) {
      if (member.readDatetime == undefined || member.readDatetime == null) {
        const _member = member as OtherUserGroupSendMessageReadMemberModel;
        const typeName: string | undefined = this.otherUserGroupTypeMap.get(_member.groupTypeId);
        _member.groupTypeName = typeName ? typeName : "";

        unreadMembers.push(_member);
      }
    }

    return unreadMembers;
  }

  /**
   * ### 既読／未読の切り替え
   */
  public async onReadSwitch(item: SendMessageReplyListTableItem): Promise<void> {
    // ローディングを表示して操作不可にしないと連打で複数かい更新が走る
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    for (const message of item.messages as Array<SendMessageReplyModel>) {
      // 変更後の値
      const afterRead: boolean = !item.readed;
      // readUserIdsに自分のユーザーIDが存在すれば現在は「既読」、存在しなければ現在は「未読」
      const currentRead: boolean = -1 < message.readUserIds.indexOf(userStore.loginUser.userId) ? true : false;
      // 変更後の値と現在の値が異なる場合のみ更新が必要
      // 例：2件返信があり、1件目は既読、2件目は未読の場合は画面上「未読」として扱っている場合、2件目だけを「既読」にしないと同じuserIdが複数登録されてしまう
      if (afterRead != currentRead) {
        const request: SendMessageRepliesMemberPatchModel = {
          readUserId: userStore.loginUser.userId,
          readUserName: userStore.loginUser.name,
          read: afterRead
        };

        await this.otherUserGroupSendMessageRepliesMembersService.patch(userStore, Number(this.message.datetime), message.groupId, message.memberId, message.datetime, request);

        if (afterRead) {
          message.readUserIds.push(userStore.loginUser.userId);
        } else {
          const index: number = message.readUserIds.indexOf(userStore.loginUser.userId);
          message.readUserIds.splice(index, 1);
        }
      }
    }
    // 最後に既読／未読を反転させる
    item.readed = !item.readed;

    this.isBusy = false;

    return;
  }
}
</script>

<style lang="scss" scoped>
.send-message-detail {
  .contents {
    height: calc(100vh - 224px);
    max-width: 1280px;
    overflow-x: auto;

    .send-message,
    .reply-messages {
      padding-top: 20px;
    }
    .send-message {
      width: 520px;
      min-width: 520px;
    }
    .reply-messages {
      width: 754px;
      min-width: 754px;
    }
  }
}
</style>
